import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../cn.js';

export type MetricTone = 'accent' | 'success' | 'warning' | 'info' | 'neutral';

const ICON_TONES: Record<MetricTone, string> = {
  accent: 'bg-accent-subtle text-accent',
  success: 'bg-success-subtle text-success',
  warning: 'bg-warning-subtle text-warning',
  info: 'bg-info-subtle text-info',
  neutral: 'bg-bg-subtle text-fg-muted',
};

const CAPTION_TONES: Record<MetricTone, string> = {
  accent: 'text-accent',
  success: 'text-success',
  warning: 'text-warning',
  info: 'text-info',
  neutral: 'text-fg-muted',
};

export interface MetricTileProps extends HTMLAttributes<HTMLDivElement> {
  /** The uppercase micro-label: "PENDING", "APPROVED", "PAID". */
  label: string;
  /** Preformatted — the tile never parses money. See the note below. */
  value: string;
  /** The line under the figure: "2 claims awaiting approval". */
  caption?: string;
  icon?: ReactNode;
  tone?: MetricTone;
}

/**
 * One headline figure on a dashboard — a pending total, an approved total, a count.
 *
 * `value` ARRIVES PREFORMATTED, as a string. The tile deliberately cannot be handed a number and
 * asked to format it: money in this system is a decimal string all the way from the Postgres
 * `numeric(18,4)` column to the screen, and a component that accepted a `number` would be an
 * invitation to parse one (CLAUDE.md invariant 2). Formatting belongs to the caller, which knows
 * the currency.
 *
 * Named for what it IS rather than where it sits, per the working agreement's naming rule — when
 * the designs change this gets restyled, not renamed.
 */
export function MetricTile({
  label,
  value,
  caption,
  icon,
  tone = 'neutral',
  className,
  ...rest
}: MetricTileProps) {
  return (
    <div
      className={cn(
        'flex items-start gap-3 rounded-xl border border-border bg-surface p-4 shadow-card',
        className,
      )}
      {...rest}
    >
      <div className="min-w-0 flex-1">
        <p className="text-[10px] font-bold uppercase tracking-wide text-fg-muted">{label}</p>
        <p className="mt-1 truncate font-display text-xl font-extrabold text-fg">{value}</p>
        {caption && (
          <p className={cn('mt-1 truncate text-xs font-medium', CAPTION_TONES[tone])}>{caption}</p>
        )}
      </div>
      {icon && (
        <span
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-lg',
            ICON_TONES[tone],
          )}
          aria-hidden="true"
        >
          {icon}
        </span>
      )}
    </div>
  );
}
